import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData, contactInfo } from '@/mocks/about';
import { useState } from 'react';

function BrandIntro() {
  return (
    <section className="py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              关于 {aboutData.brandName}
            </h1>
            <p className="text-lg text-primary-500 font-medium mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              {aboutData.tagline}
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed mb-4">
              {aboutData.intro}
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed mb-4">
              {aboutData.mission}
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed">
              {aboutData.vision}
            </p>
          </div>
          <div className="rounded-xl overflow-hidden aspect-[4/3]">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20illustration%20of%20educational%20team%20collaborating%20on%20learning%20research%2C%20warm%20amber%20and%20cream%20tones%2C%20modern%20office%20with%20books%20and%20whiteboards%2C%20clean%20editorial%20style%2C%20no%20text&width=800&height=600&seq=about-hero&orientation=landscape"
              alt="秘密教学团队"
              className="w-full h-full object-cover"
              title="秘密教学团队"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="py-10 md:py-14 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {aboutData.stats.map((stat) => (
            <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
              <p className="text-3xl md:text-4xl font-bold text-primary-500 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {stat.value}
              </p>
              <p className="text-sm text-foreground-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Team() {
  return (
    <section className="py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            核心团队
          </h2>
          <p className="text-sm text-foreground-600">一群热爱学习、致力于传播高效学习方法的教育研究者</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {aboutData.team.map((member) => (
            <div key={member.name} className="rounded-xl bg-background-50 border border-background-200/70 p-6 text-center">
              <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                  title={member.name}
                />
              </div>
              <h3 className="text-base font-semibold text-foreground-950 mb-1">{member.name}</h3>
              <p className="text-xs text-primary-500 font-medium mb-3">{member.role}</p>
              <p className="text-xs text-foreground-600 leading-relaxed">{member.bio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reputation() {
  return (
    <section className="py-10 md:py-14 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            口碑见证
          </h2>
          <p className="text-sm text-foreground-600">来自真实用户的评价和反馈</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {aboutData.testimonials.map((t) => (
            <div key={t.name} className="rounded-xl bg-background-50 border border-background-200/70 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-full h-full object-cover"
                    title={t.name}
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.role}</p>
                </div>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed">&ldquo;{t.content}&rdquo;</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MediaPartners() {
  return (
    <section className="py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            媒体报道与合作
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {aboutData.media.map((m) => (
            <div
              key={m.name}
              className="flex flex-col items-center justify-center p-5 rounded-lg bg-background-50 border border-background-200/70 text-center"
            >
              <span className="text-sm font-semibold text-foreground-950 mb-1">{m.name}</span>
              <span className="text-xs text-foreground-500">{m.type}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setName(''); setEmail(''); setMessage('');
      return;
    }

    if (message.length > 500) {
      setStatus('error');
      setErrorMsg('留言内容不能超过500个字符');
      return;
    }

    try {
      const response = await fetch(form.getAttribute('action')!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setName(''); setEmail(''); setMessage('');
      } else {
        setStatus('error');
        setErrorMsg(parsed?.meta?.message || parsed?.message || '发送失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请稍后再试');
    }
  };

  return (
    <section id="contact" className="py-10 md:py-14 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            联系我们
          </h2>
          <p className="text-sm text-foreground-600">有任何问题或合作意向，欢迎随时联系</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-950 mb-1">电子邮箱</p>
                <p className="text-sm text-foreground-600">{contactInfo.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-wechat-line text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-950 mb-1">微信公众号</p>
                <p className="text-sm text-foreground-600">{contactInfo.wechat}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-950 mb-1">办公地址</p>
                <p className="text-sm text-foreground-600">{contactInfo.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-weibo-line text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-950 mb-1">微博</p>
                <p className="text-sm text-foreground-600">{contactInfo.weibo}</p>
              </div>
            </div>
          </div>

          {status === 'success' ? (
            <div className="flex flex-col items-center justify-center p-10 rounded-xl bg-primary-50 border border-primary-200/70 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                <i className="ri-check-line text-2xl"></i>
              </div>
              <p className="text-sm font-medium text-primary-700 mb-1">发送成功！</p>
              <p className="text-xs text-primary-600">我们会尽快回复你的消息</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              action="https://readdy.ai/api/form/d9sloa14ne17ei0sdjg0"
              method="POST"
              data-readdy-form
              className="space-y-4 p-6 rounded-xl bg-background-100 border border-background-200/70"
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-950 mb-1.5">姓名</label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入你的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-950 mb-1.5">邮箱</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入你的邮箱"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-950 mb-1.5">
                  留言
                  <span className="text-foreground-400 font-normal ml-1">({message.length}/500)</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入你想说的话（最多500个字符）"
                ></textarea>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {status === 'loading' ? '发送中...' : '发送消息'}
              </button>
              {status === 'error' && (
                <p className="text-sm text-red-600 text-center">{errorMsg}</p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const aboutPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于秘密教学',
    description: aboutData.intro,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutPageJsonLd)}</script>
      <Navbar />
      <main>
        <div className="pt-20 pb-4 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>
        <BrandIntro />
        <Stats />
        <Team />
        <Reputation />
        <MediaPartners />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}